import React, { useEffect, ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Image, Button, Stack } from 'react-bootstrap';
import { map, isUndefined, isNull, camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';

import { BackButton, ClaimBadge, Loading, LoadingError, NoData } from '@components/Base';
import { PaymentMethods } from '@models/PaymentInfo';
import { ClaimableTypes } from '@models/Claim';
import { CorporateHealthClaimTypes, getCorporateHealthClaimType } from '@models/CorporateHealthClaim';
import { amountToUsd } from '@utils/currency';
import { formatDateTime } from '@utils/datetime';
import { getAttachmentType, isAttachmentImage, isAttachmentPdf } from '@utils/attachment';

import { selectCorporateHealthClaim } from './selector';
import { corporateHealthClaimFetch } from './action';
import { useEditable } from '../hook';
import { ClaimEditModal } from '../../ClaimEditModal';

interface RowItemProps {
  label: string;
  value?: string | number | ReactElement | null;
  list?: string[];
  renderItem?: (item: string) => void;
}

const RowItem = ({ label, value, list, renderItem }: RowItemProps) => {
  return (
    <Row className="mb-2">
      <Col xs={4} sm={4} md={3}>
        {label}
      </Col>
      <Col xs="auto">:</Col>
      <Col>
        <>
          {value && value}
          {list && renderItem && map(list, renderItem)}
        </>
      </Col>
    </Row>
  );
};

interface ClaimAttachmentItemProps {
  name: string;
  url: string;
}

const ClaimAttachmentItem = ({ name, url }: ClaimAttachmentItemProps) => {
  const { t } = useTranslation();
  const attachmentType = getAttachmentType(url);

  return (
    <>
      {isAttachmentImage(attachmentType) && (
        <Image src={url} thumbnail className="mb-3" data-testid={`${name}-image`} />
      )}

      {isAttachmentPdf(attachmentType) && (
        <a href={url} className="d-block mb-3" target="_blank" rel="noreferrer" data-testid={`${name}-pdf`}>
          {t('common:clickToViewDetails')}
        </a>
      )}
    </>
  );
};

const CorporateHealthClaimDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { claimableType, claimableId } = useParams();
  const { corporateHealthClaim } = useSelector(selectCorporateHealthClaim);
  const isLoaded = !isUndefined(corporateHealthClaim);
  const isError = isNull(corporateHealthClaim);
  const hasCorporateHealthClaim = isLoaded && !isNull(corporateHealthClaim);
  const shouldEdit = useEditable(corporateHealthClaim);
  const isPaymentByBankTransfer = corporateHealthClaim?.paymentInfo?.paymentMethod === PaymentMethods.BankTransfer;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleReload = () => {
    dispatch(corporateHealthClaimFetch({ id: Number(claimableId) }));
  };

  useEffect(() => {
    dispatch(corporateHealthClaimFetch({ id: Number(claimableId) }));
  }, [claimableId]);

  return (
    <>
      <BackButton className="mb-4" />
      {!isLoaded && <Loading />}
      {isError && <LoadingError />}
      {isLoaded && !hasCorporateHealthClaim && <NoData />}

      {corporateHealthClaim && (
        <Card>
          <Card.Header>
            <Stack direction="horizontal" gap={2} className="align-items-center">
              <h5 className="mb-0 me-auto">{t('common:details')}</h5>
              {shouldEdit && (
                <Button size="sm" onClick={() => setIsModalOpen(true)}>
                  {t('common:edit')}
                </Button>
              )}
            </Stack>
          </Card.Header>
          <Card.Body>
            <RowItem label={t('common:billingType')} value={corporateHealthClaim.billingTypeCode} />
            <RowItem label={t('common:status')} value={<ClaimBadge status={corporateHealthClaim.status} />} />
            <RowItem label={t('common:claimNumber')} value={corporateHealthClaim.claimNumber} />
            <RowItem
              label={t('corporateHealthClaimForm.accidentHappenedAt.label')}
              value={formatDateTime(corporateHealthClaim.accidentHappenedAt)}
            />
            <RowItem
              label={t('claimForm.claimType.label')}
              value={getCorporateHealthClaimType(corporateHealthClaim.claimType as CorporateHealthClaimTypes)}
            />
            <RowItem
              label={t('claimDetails.supportingDocuments')}
              list={corporateHealthClaim.supportingDocuments}
              renderItem={(url) => <ClaimAttachmentItem name="supporting-documents" url={url} key={url} />}
            />
            <RowItem
              label={t('common:additionalDocuments')}
              list={corporateHealthClaim.additionalDocuments}
              renderItem={(url) => <ClaimAttachmentItem key={url} name="additional-documents" url={url} />}
            />
            <RowItem
              label={t('claimForm.totalClaimedAmount.label')}
              value={amountToUsd(corporateHealthClaim.totalClaimedAmount)}
            />
            <RowItem
              label={t('claimForm.approvedAmount.label')}
              value={amountToUsd(corporateHealthClaim.approvedAmount)}
            />
            <RowItem label={t('common:note')} value={corporateHealthClaim.note} />
            <hr />
            <RowItem
              label={t('paymentInfo.paymentMethod')}
              value={t(`paymentInfo.${camelCase(corporateHealthClaim.paymentInfo?.paymentMethod)}`)}
            />
            <RowItem
              label={t('paymentInfo.beneficiaryName')}
              value={corporateHealthClaim.paymentInfo?.beneficiaryName}
            />
            {isPaymentByBankTransfer && (
              <>
                <RowItem label={t('paymentInfo.bankName')} value={corporateHealthClaim.paymentInfo?.bankName} />
                <RowItem label={t('paymentInfo.bankAccount')} value={corporateHealthClaim.paymentInfo?.bankAccount} />
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {corporateHealthClaim && (
        <ClaimEditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          claimNumber={corporateHealthClaim.claimNumber}
          claimableType={ClaimableTypes.CorporateHealth}
          claimableId={corporateHealthClaim.id}
          currentStatus={corporateHealthClaim.status}
          currentTotalClaimedAmount={corporateHealthClaim.totalClaimedAmount}
          onSuccess={handleReload}
        />
      )}
    </>
  );
};

export { CorporateHealthClaimDetails };
